import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { WhiteSpaceMap } from '../components/dashboard/WhiteSpaceMap';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';
import mapData from '../components/dashboard/map_data.json';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtM = (v) => v >= 1e6 ? '$' + (v / 1e6).toFixed(v / 1e6 >= 10 ? 0 : 1) + 'M' : '$' + (v / 1e3).toFixed(0) + 'k';

const GUIDE = {
  sections: [
    { title: 'KPI Cards', desc: "Zero-Presence Ports = overseas ports where Sharat ships nothing but competitors do. Competitor Value = total $ competitors land at those ports. White-ringed markets = ports in countries Sharat already serves.", example: "Example: 138 ports receive zero Sharat cargo, worth $236M — that's the total addressable white-space opportunity." },
    { title: 'Port List', desc: "Every port where competitors land shrimp but Sharat doesn't. Click any port to see its competitor breakdown, volume, and product mix in the detail panel.", example: 'Example: Antwerpen (Belgium) has $519M in competitor value from 195 exporters — a massive port Sharat could enter.' },
    { title: 'Detail Panel', desc: 'Shows top competitors at the selected port, their export value, and the product forms they ship. "In PD/P&D" shows how much of the port\'s value is in Sharat\'s core product.', example: 'Example: If Antwerpen shows $264M in PD/P&D, Sharat can enter with its existing product without needing new lines.' },
    { title: 'World Map', desc: 'Bubbles sized by competitor white-space value per country. White ring = Sharat already sells to that country (easier entry via existing relationships).', example: 'Example: A white-ringed US bubble means Sharat already has US buyers — routing cargo to a new US port is simpler than entering a new country.' },
  ],
  lookFor: "Ports in Sharat's existing countries (white ring) with high PD/P&D value = lowest-effort wins. Ports with few competitors but high value = less price pressure. The list is already ranked by value, biggest opportunities first.",
};

/* ── Skeleton ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const WhiteSpaceSkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="whitespace-skeleton">
    <div className="max-w-[1300px] mx-auto">
      <SB className="h-2 w-64 mb-2" delay={0} />
      <SB className="h-6 w-[560px] mb-1.5" delay={30} />
      <SB className="h-3 w-[620px] mb-4" delay={60} />
      <div className="grid grid-cols-5 gap-3 mb-4">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="rounded-2xl p-3.5" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
            <SB className="h-2 w-16 mb-2.5" delay={80 + i * 30} />
            <SB className="h-5 w-14" delay={100 + i * 30} />
          </div>
        ))}
      </div>
      <div className="rounded-2xl relative h-[400px] mb-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
        {[
          { x: '22%', y: '28%', s: 44 }, { x: '50%', y: '22%', s: 28 },
          { x: '76%', y: '34%', s: 36 }, { x: '60%', y: '44%', s: 18 },
        ].map((b, i) => (
          <div key={i} className="skel-bone skel-round absolute" style={{ left: b.x, top: b.y, width: b.s, height: b.s, animationDelay: `${240 + i * 50}ms` }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        <div className="rounded-2xl p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
          {Array.from({ length: 8 }, (_, i) => <SB key={i} className="h-9 w-full mb-1.5" delay={500 + i * 25} />)}
        </div>
        <div className="rounded-2xl p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
          <SB className="h-4 w-40 mb-3" delay={550} />
          {Array.from({ length: 5 }, (_, i) => <SB key={i} className="h-3 w-full mb-2" delay={570 + i * 25} />)}
        </div>
      </div>
    </div>
  </div>
);

/* ── Ranked bar list (Competitors / Product mix) ── */
const RankBars = ({ items, max }) => (
  <div className="flex flex-col gap-1.5">
    {items.map((it, i) => (
      <div key={it.name + i} className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 text-xs">
        <span className="truncate text-dash-ink" title={it.name}>{it.name}</span>
        <span className="font-mono text-[11px] text-dash-mut text-right">{fmtM(it.val)}</span>
        <div className="col-span-2 h-1.5 bg-dash-panel2 rounded overflow-hidden">
          <div className="h-full rounded" style={{ width: `${(it.val / max * 100).toFixed(0)}%`, background: 'linear-gradient(90deg,#F2C46C,#F2B14C)' }} />
        </div>
      </div>
    ))}
  </div>
);

/* ── Main Page ── */
export default function WhiteSpacePortsReact() {
  const [data, setData] = useState(null);
  const [origData, setOrigData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'market'
  const [countryFilter, setCountryFilter] = useState(null);
  const [selectedPortId, setSelectedPortId] = useState(null);

  const [months, setMonths] = useState([]);
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [periodLoading, setPeriodLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/static/whitespace_ports_full.json`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
      fetch(`${API}/api/dashboard-data/months`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
    ]).then(([full, mon]) => {
      setData(full);
      setOrigData(full);
      setMonths(mon || []);
      setToIdx((mon?.length || 1) - 1);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const applyPeriod = useCallback(() => {
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setPeriodLoading(true);
    const selMonths = months.slice(fromIdx, toIdx + 1).join(',');
    fetch(`${API}/api/dashboard-data/whitespace?months=${encodeURIComponent(selMonths)}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => {
        if (d.error) { alert(d.error); setPeriodLoading(false); return; }
        setData(d);
        setIsFiltered(true);
        setPeriodLoading(false);
      })
      .catch(e => { alert('Failed to apply period: ' + e.message); setPeriodLoading(false); });
  }, [months, fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    if (!origData) return;
    setData(origData);
    setIsFiltered(false);
  }, [origData]);

  // Countries where Sharat already sells — used both for the map's white-ring
  // overlay and to flag each port as "in one of our markets".
  const inMarketSet = useMemo(() => new Set((data?.countries || []).filter(c => c.in_market).map(c => c.name)), [data]);

  // Ports with zero Sharat presence, enriched with an in_market flag derived
  // from their country (the raw data only carries in_market at country level).
  const zeroPorts = useMemo(() => {
    if (!data) return [];
    return data.ports.filter(p => !p.in_sharat).map(p => ({ ...p, in_market: inMarketSet.has(p.country) }));
  }, [data, inMarketSet]);

  const filteredPorts = useMemo(() => {
    let list = zeroPorts;
    if (filter === 'market') list = list.filter(p => p.in_market);
    if (countryFilter) list = list.filter(p => p.country === countryFilter);
    return list;
  }, [zeroPorts, filter, countryFilter]);

  const selectedPort = useMemo(() => {
    const found = filteredPorts.find(p => p.name + p.country === selectedPortId);
    return found || filteredPorts[0] || null;
  }, [filteredPorts, selectedPortId]);

  const handleToggleFilter = useCallback((f) => {
    setFilter(f);
    setSelectedPortId(null);
  }, []);

  const handleSelectCountry = useCallback((name) => {
    setCountryFilter(prev => prev === name ? null : name);
  }, []);

  if (loading) return <WhiteSpaceSkeleton />;
  if (error) return <div className="h-full bg-dash-bg flex items-center justify-center"><div className="text-dash-coral font-mono text-sm">Error: {error}</div></div>;

  const k = data.kpi;
  const sharatCountryCount = new Set(data.sharat_ports.map(p => p.country)).size;
  const kpiCards = [
    { label: 'Zero-presence ports', value: k.zero_ports, sub: 'overseas arrival ports', accent: 'var(--dash-amber)' },
    { label: 'Competitor value', value: fmtM(k.opp_val), sub: `${k.opp_qty.toLocaleString()} t landed`, accent: 'var(--dash-coral)' },
    { label: "In Sharat's markets", value: fmtM(k.inmkt_val), sub: `${k.inmkt_ports} ports · ${sharatCountryCount} countries`, accent: 'var(--dash-ice)' },
    { label: "In Sharat's product", value: fmtM(k.pd_val), sub: 'PD / P&D', accent: '#8FD694' },
    { label: "Sharat's ports today", value: data.sharat_ports.length, sub: 'arrival ports', accent: 'var(--dash-mut)' },
  ];

  const totalVal = filteredPorts.reduce((a, p) => a + p.val, 0);

  const periodLabel = isFiltered
    ? `${months[fromIdx]} to ${months[toIdx]} (${toIdx - fromIdx + 1} month${toIdx - fromIdx === 0 ? '' : 's'})`
    : `All ${months.length || 23} months · ${months[0]} to ${months[months.length - 1]}`;

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="whitespace-ports-react">
      <div className="max-w-[1300px] mx-auto">

        {/* Header */}
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-dash-amber mb-2">
          White-Space Destination Ports &middot; Apr 2024 – Feb 2026
        </div>
        <h1 className="font-grotesk font-extrabold text-2xl tracking-tight mb-1">
          Overseas markets where <span className="text-dash-amber">Sharat is absent</span> but competitors land cargo
        </h1>
        <p className="text-dash-mut text-[13px] max-w-[840px]">
          Sharat&rsquo;s shrimp arrives at only {data.sharat_ports.length} destination ports. The map shows <b className="text-dash-ink">competitor white-space value by destination country</b> — click a country to see its zero-Sharat ports. Bubble size = competitor value &middot; colour = region &middot; white ring = Sharat already sells there.
        </p>
        <div className="inline-block mt-2.5 bg-[rgba(242,177,76,0.08)] border border-[rgba(242,177,76,0.28)] rounded-lg px-3 py-2 text-xs text-[#F4D9A6]">
          ⚠ One month (Apr 2024 – Feb 2026): shows competitor presence &amp; size, not growth.
        </div>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="mt-3.5">
            <PeriodFilter
              months={months}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
              loading={periodLoading}
            />
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 my-4" data-testid="kpi-cards-whitespace">
          {kpiCards.map((c, i) => (
            <div key={i} className="bg-gradient-to-br from-dash-panel to-dash-panel2 border border-dash-line rounded-[14px] p-3.5 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: c.accent }} />
              <div className="text-[10px] tracking-[0.12em] uppercase text-dash-mut mb-2">{c.label}</div>
              <div className="font-grotesk font-bold text-[22px] leading-none text-dash-ink">{c.value}</div>
              <div className="font-mono text-[10.5px] text-[#5C7980] mt-1.5">{c.sub}</div>
            </div>
          ))}
        </div>

        {/* World Map */}
        <div className="bg-dash-panel border border-dash-line rounded-2xl p-4">
          <div className="font-grotesk font-bold text-sm mb-0.5">White-space world map — by destination country</div>
          <div className="text-dash-mut text-xs mb-3">Click a country bubble to filter the port list below. Zoom/scroll to separate clusters; names appear as you zoom in.</div>
          <WhiteSpaceMap countries={data.countries} selected={countryFilter} onSelect={handleSelectCountry} />
        </div>

        {/* Port list + Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-3.5 mt-3.5 items-stretch">
          <div className="bg-dash-panel border border-dash-line rounded-2xl p-4 flex flex-col">
            <div className="font-grotesk font-bold text-sm mb-0.5">Zero-Sharat destination ports</div>
            <div className="text-dash-mut text-xs mb-2.5" data-testid="port-list-caption">
              {filteredPorts.length} ports · {fmtM(totalVal)} competitor value{countryFilter ? ` · ${countryFilter}` : ''}
            </div>
            <div className="flex items-center gap-2 mb-2.5 flex-wrap" data-testid="whitespace-toolbar">
              <div className="flex bg-dash-panel2 border border-dash-line rounded-lg p-0.5 gap-0.5">
                <button
                  onClick={() => handleToggleFilter('all')}
                  className={`font-mono text-[11px] px-3 py-1.5 rounded-md transition ${filter === 'all' ? 'bg-dash-amber text-[#241a06] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
                >
                  All ports
                </button>
                <button
                  onClick={() => handleToggleFilter('market')}
                  className={`font-mono text-[11px] px-3 py-1.5 rounded-md transition ${filter === 'market' ? 'bg-dash-amber text-[#241a06] font-bold' : 'text-dash-mut hover:text-dash-ink'}`}
                >
                  In Sharat&rsquo;s markets
                </button>
              </div>
              {countryFilter && (
                <button
                  onClick={() => setCountryFilter(null)}
                  className="font-mono text-[11px] text-dash-ice bg-transparent border border-dash-line rounded-lg px-2.5 py-1.5 cursor-pointer hover:border-dash-ice transition"
                  data-testid="clear-country-filter"
                >
                  ✕ clear country
                </button>
              )}
            </div>
            <div className="flex flex-col gap-1.5 flex-1 min-h-[200px] max-h-[600px] overflow-y-auto pr-1" data-testid="whitespace-port-list">
              {filteredPorts.length === 0 && (
                <div className="text-dash-mut text-xs text-center py-8">No ports in this filter</div>
              )}
              {filteredPorts.map((p, i) => {
                const id = p.name + p.country;
                const isSel = selectedPort && (selectedPort.name + selectedPort.country) === id;
                return (
                  <div
                    key={id}
                    onClick={() => setSelectedPortId(id)}
                    className={`grid grid-cols-[18px_1fr_auto] gap-2.5 items-center px-2.5 py-2 rounded-lg border cursor-pointer transition ${isSel ? 'bg-[rgba(242,177,76,0.12)] border-[#B07C24]' : 'border-transparent hover:bg-white/[0.04]'}`}
                    data-testid={`port-row-${id}`}
                  >
                    <span className="font-mono text-[10px] text-[#5C7980] text-right">{i + 1}</span>
                    <span>
                      <div className="text-[13px] text-dash-ink">
                        {p.name}
                        {p.in_market && <span className="font-mono text-[9px] text-[#8FD694] border border-[rgba(143,214,148,0.4)] rounded px-1 ml-1.5">OUR MARKET</span>}
                      </div>
                      <div className="font-mono text-[10px] text-dash-mut">{p.country} · {p.exporters} exporters</div>
                    </span>
                    <span className="font-mono text-xs font-bold text-dash-amber text-right">{fmtM(p.val)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="bg-dash-panel border border-dash-line rounded-2xl p-4" data-testid="whitespace-detail">
            {!selectedPort ? (
              <div className="text-dash-mut text-xs">No port in this filter.</div>
            ) : (
              <>
                {(() => {
                  const region = mapData.coords[selectedPort.country]?.region || '';
                  const forms = Object.entries(selectedPort.forms || {}).map(([name, val]) => ({ name, val })).sort((a, b) => b.val - a.val);
                  const competitors = selectedPort.competitors || [];
                  const cMax = Math.max(...competitors.map(x => x.val), 1);
                  const fMax = Math.max(...forms.map(x => x.val), 1);
                  const pdVal = (selectedPort.forms?.['PD (Tail-Off)'] || 0) + (selectedPort.forms?.['PD / P&D'] || 0) + (selectedPort.forms?.['PDTO'] || 0);
                  return (
                    <>
                      <div className="flex flex-wrap gap-2.5 items-baseline mb-1">
                        <h3 className="font-grotesk font-extrabold text-lg">{selectedPort.name}</h3>
                        <span className="font-mono text-[11px] text-dash-mut uppercase">{selectedPort.country} · {region}</span>
                        {selectedPort.in_market
                          ? <span className="font-mono text-[11px] text-[#8FD694]">● our market</span>
                          : <span className="font-mono text-[11px] text-dash-amber">● new market</span>}
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 my-3">
                        <div className="bg-white/[0.025] border border-dash-line rounded-[11px] px-3 py-2.5">
                          <div className="text-[9.5px] tracking-[0.1em] uppercase text-dash-mut mb-1.5">Competitor value</div>
                          <div className="font-grotesk font-bold text-[17px]">{fmtM(selectedPort.val)}</div>
                        </div>
                        <div className="bg-white/[0.025] border border-dash-line rounded-[11px] px-3 py-2.5">
                          <div className="text-[9.5px] tracking-[0.1em] uppercase text-dash-mut mb-1.5">Competitors</div>
                          <div className="font-grotesk font-bold text-[17px]">{selectedPort.exporters} <small className="text-dash-mut font-normal text-xs">exporters</small></div>
                        </div>
                        <div className="bg-white/[0.025] border border-dash-line rounded-[11px] px-3 py-2.5">
                          <div className="text-[9.5px] tracking-[0.1em] uppercase text-dash-mut mb-1.5">Volume landed</div>
                          <div className="font-grotesk font-bold text-[17px]">{selectedPort.qty} <small className="text-dash-mut font-normal text-xs">t</small></div>
                        </div>
                        <div className="bg-white/[0.025] border border-dash-line rounded-[11px] px-3 py-2.5">
                          <div className="text-[9.5px] tracking-[0.1em] uppercase text-dash-mut mb-1.5">In PD / P&amp;D</div>
                          <div className="font-grotesk font-bold text-[17px]">{fmtM(pdVal)}</div>
                        </div>
                      </div>
                      <h4 className="font-mono text-[10px] tracking-[0.08em] uppercase text-dash-ice my-2.5">Top competitors shipping here</h4>
                      <RankBars items={competitors} max={cMax} />
                      <h4 className="font-mono text-[10px] tracking-[0.08em] uppercase text-dash-ice my-2.5">Product mix landed</h4>
                      <RankBars items={forms} max={fMax} />
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 border-t border-dash-line pt-4 text-[#5C7980] text-[11.5px] leading-relaxed">
          <b>Method:</b> &ldquo;zero presence&rdquo; = no Sharat shipment to that Port of Destination in Apr 2024 – Feb 2026. Map bubbles aggregate competitor white-space value to each destination <b>country</b>; the list below is port-level. White ring = country where Sharat already sells.
        </div>

        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
